import { DndContext, PointerSensor, useSensor, useSensors, type DragEndEvent } from "@dnd-kit/core";
import { arrayMove, horizontalListSortingStrategy, SortableContext, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { fieldKey, parse, type FieldDef, type IssueSnapshot, type SchemaIndex } from "@gjwtrack/query-language";
import { useVirtualizer } from "@tanstack/react-virtual";
import { ChevronDown, ChevronUp, ChevronsUpDown, GripVertical, Plus, Star, Trash2 } from "lucide-react";
import { useMemo, useRef, useState, type ReactNode } from "react";
import { Link, useNavigate } from "react-router";
import type { UserSettings } from "@gjwtrack/domain";
import { client, useClient } from "../lib/client.ts";
import { BUNDLE_KINDS, fieldValueText, issueKey, tableTime, userName } from "../lib/format.ts";
import { Checkbox, Select, SelectType, type SelectItem } from "../ui/ring.ts";
import { Avatar, ValueBadge } from "./Badges.tsx";

/** Built-in columns are named; field columns are `f:<lower-cased field name or project alias>`. */
export type ColumnId = string;

/** Column widths are proportional (`fr`) with a floor; the table scrolls sideways only below the sum of floors. */
interface Width {
  min: number; // rem
  fr: number;
}
const BUILTIN: { id: ColumnId; label: string; sort?: string; width: Width }[] = [
  { id: "created", label: "Created", sort: "created", width: { min: 8.5, fr: 1.1 } },
  { id: "updated", label: "Updated", sort: "updated", width: { min: 8.5, fr: 1.1 } },
  { id: "resolved", label: "Resolved", sort: "resolved", width: { min: 8.5, fr: 1.1 } },
  { id: "reporter", label: "Created by", width: { min: 8, fr: 1.2 } },
  { id: "tags", label: "Tags", width: { min: 8, fr: 1.4 } },
];
export const DEFAULT_COLUMNS: ColumnId[] = ["f:state", "created", "f:type", "f:reviewer", "f:assignee"];
const STORAGE = "gjw.issues.columns";
const ROW = 40;

/** Columns come from the user's server-side settings (as in YouTrack); a pre-settings localStorage copy is picked up once. */
function initialColumns(settings: UserSettings): ColumnId[] {
  if (settings.issueColumns) return settings.issueColumns;
  try {
    const raw = localStorage.getItem(STORAGE);
    const v: unknown = raw ? JSON.parse(raw) : null;
    if (Array.isArray(v) && v.every((x) => typeof x === "string")) return v as ColumnId[];
  } catch {
    /* ignore */
  }
  return DEFAULT_COLUMNS;
}

/** One field column: the same name (or project alias) may be different field defs in different projects. */
interface FieldColumn {
  id: ColumnId;
  label: string;
  byProject: Map<number, FieldDef>;
  kind: string;
}

function fieldCatalogue(schema: SchemaIndex): Map<ColumnId, FieldColumn> {
  const out = new Map<ColumnId, FieldColumn>();
  for (const [projectId, pfs] of schema.projectFieldsByProject) {
    for (const pf of pfs) {
      const def = schema.fieldsById.get(pf.fieldId);
      if (!def) continue;
      const label = pf.alias ?? def.name;
      const id = `f:${label.toLowerCase()}`;
      let col = out.get(id);
      if (!col) {
        col = { id, label, byProject: new Map(), kind: def.kind };
        out.set(id, col);
      }
      col.byProject.set(projectId, def);
    }
  }
  return out;
}

const widthFor = (kind: string): Width =>
  kind === "user" || kind === "group"
    ? { min: 8, fr: 1.2 }
    : kind === "date" || kind === "datetime"
      ? { min: 8.5, fr: 1 }
      : kind === "period" || kind === "integer" || kind === "float"
        ? { min: 5, fr: 0.6 }
        : kind === "string" || kind === "text"
          ? { min: 8, fr: 1.4 }
          : { min: 6, fr: 1 };
const track = (w: Width): string => `minmax(${w.min}rem, ${w.fr}fr)`;

const quoteAttr = (s: string): string => (/[\s:,]/.test(s) ? `{${s}}` : s);

function currentSort(q: string): { attr: string; dir: "asc" | "desc" } | null {
  try {
    const s = parse(q).sort[0];
    return s ? { attr: s.attr.replace(/^\{|\}$/g, "").toLowerCase(), dir: s.dir } : null;
  } catch {
    return null;
  }
}

/** Replace the query's sort clause; `attrs` may be several ("project, number" for the ID column). */
function withSort(q: string, attrs: string[], dir: "asc" | "desc"): string {
  const base = q.replace(/\s*\b(sort|order)\s+by:.*$/i, "").trim();
  return `${base}${base ? " " : ""}sort by: ${attrs.map((a) => `${quoteAttr(a)} ${dir}`).join(", ")}`;
}

interface Props {
  ids: number[];
  query: string;
  onQuery: (q: string) => void;
  /** Rendered below the last row, inside the scroll area (the pager). */
  footer?: ReactNode;
}

/** YouTrack's table view: star · checkbox · ID · Summary · chosen columns · "+" to add more. Header sorts through `sort by:`. */
export function IssueTable({ ids, query, onQuery, footer }: Props) {
  const schema = useClient((s) => s.schema)!;
  const me = useClient((s) => s.me);
  const columns = useMemo(() => initialColumns(me?.settings ?? {}), [me?.settings]);
  const setColumns = (next: ColumnId[]): void => {
    try {
      localStorage.removeItem(STORAGE);
    } catch {
      /* ignore */
    }
    void client.setUserSettings({ issueColumns: next });
  };
  const [selected, setSelected] = useState<ReadonlySet<number>>(new Set());
  const catalogue = useMemo(() => fieldCatalogue(schema), [schema]);
  const priority = catalogue.get("f:priority");
  const sort = currentSort(query);

  const cols = columns
    .map((id) => {
      const b = BUILTIN.find((x) => x.id === id);
      if (b) return { id, label: b.label, sort: b.sort, width: b.width, field: undefined as FieldColumn | undefined };
      const f = catalogue.get(id);
      return f ? { id, label: f.label, sort: f.label, width: widthFor(f.kind), field: f } : null;
    })
    .filter((c): c is NonNullable<typeof c> => c !== null);
  const template = `36px 32px 6.5rem minmax(12rem, 3fr) ${cols.map((c) => track(c.width)).join(" ")} 44px`;
  const minWidth = `calc(112px + ${18.5 + cols.reduce((n, c) => n + c.width.min, 0)}rem)`;

  const toggleColumn = (id: ColumnId): void => {
    setColumns(columns.includes(id) ? columns.filter((c) => c !== id) : [...columns, id]);
  };
  // YouTrack's column picker: one alphabetical list of every attribute with a "Filter items" box (Ring Select); a click adds the column (or removes it if already shown).
  const pickerData: SelectItem[] = [...BUILTIN.map((b) => ({ key: b.id, label: b.label })), ...[...catalogue.values()].map((f) => ({ key: f.id, label: f.label }))].sort((a, b) =>
    a.label.localeCompare(b.label, undefined, { sensitivity: "base" }),
  );
  const sortBy = (attrs: string[] | undefined): void => {
    if (!attrs) return;
    const active = sort && sort.attr === attrs[0]!.toLowerCase();
    onQuery(withSort(query, attrs, active && sort.dir === "asc" ? "desc" : "asc"));
  };
  const allSelected = ids.length > 0 && ids.every((id) => selected.has(id));
  const sortButton = (label: string, attrs: string[] | undefined) => {
    const active = sort !== null && attrs !== undefined && sort.attr === attrs[0]!.toLowerCase();
    return (
      <button
        className={`flex min-w-0 items-center gap-1 truncate text-left text-[13px] ${active ? "font-semibold text-ink" : "text-muted"} ${attrs ? "hover:text-ink" : "cursor-default"}`}
        onClick={() => sortBy(attrs)}
        disabled={!attrs}
        title={attrs ? `Sort by ${label}` : undefined}
      >
        <span className="truncate">{label}</span>
        {attrs ? (active ? sort!.dir === "asc" ? <ChevronUp size={14} /> : <ChevronDown size={14} /> : <ChevronsUpDown size={14} className="text-faint" />) : null}
      </button>
    );
  };
  const header = (label: string, attrs: string[] | undefined, key: string) => <span key={key}>{sortButton(label, attrs)}</span>;
  // Chosen columns reorder by dragging the grip that appears on hover (YouTrack's ⋮⋮), the bin on the right removes the column.
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));
  const onDragEnd = (e: DragEndEvent): void => {
    const over = e.over?.id;
    if (over === undefined || over === e.active.id) return;
    const from = columns.indexOf(String(e.active.id));
    const to = columns.indexOf(String(over));
    if (from < 0 || to < 0) return;
    setColumns(arrayMove(columns, from, to));
  };

  const parentRef = useRef<HTMLDivElement>(null);
  const v = useVirtualizer({ count: ids.length, getScrollElement: () => parentRef.current, estimateSize: () => ROW, overscan: 12 });

  return (
    <div ref={parentRef} className="min-h-0 flex-1 overflow-auto">
      <div style={{ minWidth }}>
        <div className="sticky top-0 z-[1] grid h-9 items-center border-b border-line bg-ground px-4" style={{ gridTemplateColumns: template }} role="row">
          <span />
          <span className="flex items-center">
            <Checkbox checked={allSelected} onChange={() => setSelected(allSelected ? new Set() : new Set(ids))} aria-label="Select all" />
          </span>
          {header("ID", ["project", "number"], "id")}
          {header("Summary", ["summary"], "summary")}
          <DndContext sensors={sensors} onDragEnd={onDragEnd}>
            <SortableContext items={cols.map((c) => c.id)} strategy={horizontalListSortingStrategy}>
              {cols.map((c) => (
                <ColumnHeader key={c.id} id={c.id} onRemove={() => toggleColumn(c.id)}>
                  {sortButton(c.label, c.sort ? [c.sort] : undefined)}
                </ColumnHeader>
              ))}
            </SortableContext>
          </DndContext>
          <span className="flex justify-end">
            <Select
              filter
              type={SelectType.CUSTOM}
              data={pickerData}
              selected={null}
              minWidth={320}
              onSelect={(item) => item && toggleColumn(String(item.key))}
              customAnchor={({ wrapperProps, buttonProps, popup }) => (
                <span {...wrapperProps} className="relative inline-flex">
                  <button {...buttonProps} className="icon-btn h-7 w-7" title="Add column">
                    <Plus size={18} />
                  </button>
                  {popup}
                </span>
              )}
            />
          </span>
        </div>
        {ids.length === 0 ? (
          <div className="px-4 py-8 text-center text-muted">No issues match.</div>
        ) : (
          <div className="relative" style={{ height: v.getTotalSize() }}>
            {v.getVirtualItems().map((row) => (
              <div key={ids[row.index]} className="absolute left-0 w-full" style={{ top: row.start, height: ROW }}>
                <Row
                  id={ids[row.index]!}
                  schema={schema}
                  meId={me?.id}
                  template={template}
                  cols={cols}
                  priority={priority}
                  selected={selected.has(ids[row.index]!)}
                  onSelect={(on) =>
                    setSelected((s) => {
                      const next = new Set(s);
                      if (on) next.add(ids[row.index]!);
                      else next.delete(ids[row.index]!);
                      return next;
                    })
                  }
                />
              </div>
            ))}
          </div>
        )}
        {footer}
      </div>
    </div>
  );
}

/** A chosen column's header: hover shows the drag grip on the left and the remove bin on the right, as in YouTrack. */
function ColumnHeader({ id, onRemove, children }: { id: ColumnId; onRemove: () => void; children: ReactNode }) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({ id });
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Translate.toString(transform), transition }}
      className={`group/col relative -ml-5 inline-flex max-w-full items-center justify-self-start rounded pr-0.5 hover:z-[3] hover:bg-surface ${isDragging ? "z-[2] bg-surface shadow" : ""}`}
    >
      <button
        ref={setActivatorNodeRef}
        {...attributes}
        {...listeners}
        className="flex h-6 w-5 shrink-0 cursor-grab items-center justify-center text-muted opacity-0 group-hover/col:opacity-100 active:cursor-grabbing"
        title="Drag to reorder"
        tabIndex={-1}
      >
        <GripVertical size={14} />
      </button>
      {children}
      <button className="ml-1 flex h-6 w-6 shrink-0 items-center justify-center rounded text-muted opacity-0 group-hover/col:opacity-100 hover:text-danger" title="Remove column" onClick={onRemove}>
        <Trash2 size={14} />
      </button>
    </div>
  );
}

interface RowProps {
  id: number;
  schema: SchemaIndex;
  meId: number | undefined;
  template: string;
  cols: { id: ColumnId; field: FieldColumn | undefined }[];
  priority: FieldColumn | undefined;
  selected: boolean;
  onSelect: (on: boolean) => void;
}

function Row({ id, schema, meId, template, cols, priority, selected, onSelect }: RowProps) {
  const issue = useClient((s) => s.display[id]);
  const pending = useClient((s) => s.pending.some((p) => p.issueId === id));
  const navigate = useNavigate();
  if (!issue) return null;
  const key = issueKey(schema, issue);
  const resolved = issue.resolvedAt !== null;
  const watching = meId !== undefined && issue.watchers.includes(meId);
  const prioDef = priority?.byProject.get(issue.projectId);
  const prioVal = prioDef ? issue.fields[fieldKey(prioDef.id)]?.[0] : undefined;
  return (
    <div
      role="row"
      data-issue-id={issue.id}
      data-issue-key={key}
      className={`group grid h-full cursor-pointer items-center border-b border-line px-4 hover:bg-surface ${pending ? "opacity-70" : ""} ${resolved ? "row-resolved" : ""}`}
      style={{ gridTemplateColumns: template }}
      onClick={() => navigate(`/issue/${key}`)}
    >
      <button
        className={`flex h-7 w-7 items-center justify-center rounded ${watching ? "text-star" : "text-faint opacity-0 group-hover:opacity-100 hover:text-star"}`}
        title={watching ? "Stop watching" : "Watch this issue"}
        onClick={(e) => {
          e.stopPropagation();
          void client.watch(issue.id, !watching);
        }}
      >
        <Star size={16} fill={watching ? "currentColor" : "none"} />
      </button>
      <span className="flex items-center" onClick={(e) => e.stopPropagation()}>
        <Checkbox checked={selected} onChange={(e) => onSelect(e.target.checked)} aria-label={`Select ${key}`} />
      </span>
      <Link to={`/issue/${key}`} className={`truncate text-[14px] ${resolved ? "text-faint line-through" : "text-link"}`} onClick={(e) => e.stopPropagation()}>
        {key}
      </Link>
      <span className="flex min-w-0 items-center gap-2 pr-4">
        {prioDef && prioVal !== undefined && (
          <span className="flex shrink-0">
            <ValueBadge schema={schema} def={prioDef} value={prioVal} size={20} />
          </span>
        )}
        <Link to={`/issue/${key}`} className={`truncate text-[14px] ${resolved ? "text-muted" : "text-ink"}`} onClick={(e) => e.stopPropagation()}>
          {issue.summary}
        </Link>
      </span>
      {cols.map((c) => (
        <span key={c.id} className="min-w-0 truncate pr-3 text-[14px]">
          {c.field ? <FieldCell schema={schema} issue={issue} col={c.field} resolved={resolved} /> : <BuiltinCell id={c.id} schema={schema} issue={issue} resolved={resolved} />}
        </span>
      ))}
      <span />
    </div>
  );
}

function BuiltinCell({ id, schema, issue, resolved }: { id: ColumnId; schema: SchemaIndex; issue: IssueSnapshot; resolved: boolean }) {
  const tone = resolved ? "text-muted" : "text-ink";
  switch (id) {
    case "created":
      return <span className={tone}>{tableTime(issue.createdAt)}</span>;
    case "updated":
      return <span className={tone}>{tableTime(issue.updatedAt)}</span>;
    case "resolved":
      return issue.resolvedAt === null ? null : <span className={tone}>{tableTime(issue.resolvedAt)}</span>;
    case "reporter":
      return <UserCell schema={schema} userId={issue.reporterId} resolved={resolved} />;
    case "tags":
      return (
        <span className="flex gap-1">
          {issue.tags.map((t) => (
            <span key={t} className="chip">
              {schema.tagsById.get(t)?.name ?? `#${t}`}
            </span>
          ))}
        </span>
      );
    default:
      return null;
  }
}

function UserCell({ schema, userId, resolved }: { schema: SchemaIndex; userId: number; resolved: boolean }) {
  return (
    <span className="inline-flex items-center gap-1.5 align-middle">
      <Avatar schema={schema} userId={userId} size={20} />
      <span className={resolved ? "text-muted" : "text-value"}>{userName(schema, userId)}</span>
    </span>
  );
}

function FieldCell({ schema, issue, col, resolved }: { schema: SchemaIndex; issue: IssueSnapshot; col: FieldColumn; resolved: boolean }) {
  const def = col.byProject.get(issue.projectId);
  if (!def) return null; // the project does not have this field: blank, as in YouTrack
  const vals = issue.fields[fieldKey(def.id)] ?? [];
  if (def.kind === "user") {
    if (vals.length === 0) {
      const empty = col.label.toLowerCase() === "assignee" ? "Unassigned" : `No ${col.label.toLowerCase()}`;
      return (
        <span className="inline-flex items-center gap-1.5 align-middle text-muted">
          <span className="badge round empty" style={{ width: 20, height: 20, fontSize: 11 }} aria-hidden>
            ?
          </span>
          {empty}
        </span>
      );
    }
    return (
      <span className="flex flex-wrap gap-x-3 gap-y-0.5">
        {vals.map((v) => (typeof v === "number" ? <UserCell key={v} schema={schema} userId={v} resolved={resolved} /> : <span key={v}>{String(v)}</span>))}
      </span>
    );
  }
  if (BUNDLE_KINDS.has(def.kind)) {
    return (
      <>
        {vals.map((v, i) => {
          const el = typeof v === "number" ? schema.elementsById.get(v) : undefined;
          const slot = el?.colorId ?? 0;
          return (
            <span key={String(v)} className={`tv ${slot > 0 ? `chip-${((slot - 1) % 8) + 1}` : ""} ${resolved ? "text-muted" : slot > 0 ? "" : "text-value"}`}>
              {i > 0 && ", "}
              {fieldValueText(schema, def, v)}
            </span>
          );
        })}
      </>
    );
  }
  return <span className={resolved ? "text-muted" : "text-ink"}>{vals.map((v) => fieldValueText(schema, def, v)).join(", ")}</span>;
}
